/**
 * Tema de la interfaz: claro, oscuro o el del sistema.
 *
 * La preferencia se guarda en `localStorage` (`middle:theme`, por defecto
 * "sistema"). Un tema explicito queda escrito en `<html data-theme="...">`;
 * "sistema" quita el atributo y deja mandar a `prefers-color-scheme`. Los tokens
 * de `styles/global.css` cuelgan de eso, y el `<script>` inline de `index.html`
 * aplica lo guardado antes del primer pintado para que no parpadee.
 *
 * POR QUE UN STORE DE MODULO Y NO ESTADO DE UN PROVIDER: el tema es del
 * documento entero, no de un subarbol. Con `useSyncExternalStore` cualquier
 * componente —el lienzo del flujo, el boton de Google, el selector del menu— lo
 * lee igual, este donde este montado, y los tests de una pantalla no necesitan
 * envolverla en nada. Es el mismo patron de `auth/token-store.ts`.
 *
 * `ThemeProvider` va una sola vez en la raiz (main.tsx): sincroniza el documento
 * al arrancar y mantiene escuchando al sistema operativo y a las otras pestanas
 * mientras la app esta abierta.
 */
import { useLayoutEffect, useMemo, useSyncExternalStore } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { readLocalPreference, writeLocalPreference } from './local-preferences';

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'middle:theme';

/**
 * Color de la barra del navegador en movil (`<meta name="theme-color">`).
 * Es el fondo del menu: mantener igual a `--surface-sidebar` de global.css.
 */
export const THEME_COLORS: Readonly<Record<ResolvedTheme, string>> = {
  light: '#fcfcfd',
  dark: '#0f1115',
};

export interface ThemeState {
  /** Lo que eligio el usuario. */
  readonly preference: ThemePreference;
  /** El tema que se esta viendo: la preferencia, o el del sistema si es "sistema". */
  readonly resolvedTheme: ResolvedTheme;
  readonly setPreference: (preference: ThemePreference) => void;
}

interface ThemeSnapshot {
  readonly preference: ThemePreference;
  readonly systemTheme: ResolvedTheme;
}

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function resolveTheme(
  preference: ThemePreference,
  systemTheme: ResolvedTheme,
): ResolvedTheme {
  return preference === 'system' ? systemTheme : preference;
}

/**
 * Eleccion hecha en esta pestana que el almacenamiento no pudo guardar. Manda
 * sobre lo guardado: si no, el cambio se desharia en el render siguiente.
 */
let unsavedPreference: ThemePreference | null = null;

function readPreference(): ThemePreference {
  if (unsavedPreference !== null) {
    return unsavedPreference;
  }
  const stored = readLocalPreference(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : 'system';
}

/** `null` en navegadores sin `matchMedia`: ahi el sistema se toma como claro. */
function systemQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_SCHEME_QUERY) : null;
}

function readSystemTheme(): ResolvedTheme {
  return systemQuery()?.matches === true ? 'dark' : 'light';
}

function snapshotsFor(preference: ThemePreference): Record<ResolvedTheme, ThemeSnapshot> {
  return {
    light: { preference, systemTheme: 'light' },
    dark: { preference, systemTheme: 'dark' },
  };
}

/** Una instancia por combinacion: `useSyncExternalStore` exige la misma referencia si nada cambio. */
const SNAPSHOTS: Readonly<Record<ThemePreference, Record<ResolvedTheme, ThemeSnapshot>>> = {
  light: snapshotsFor('light'),
  dark: snapshotsFor('dark'),
  system: snapshotsFor('system'),
};

function getSnapshot(): ThemeSnapshot {
  return SNAPSHOTS[readPreference()][readSystemTheme()];
}

/** Escribe el tema vigente en el documento: atributo de `<html>` y color de la barra movil. */
function applyToDocument(): void {
  const preference = readPreference();
  const root = document.documentElement;
  if (preference === 'system') {
    delete root.dataset['theme'];
  } else {
    root.dataset['theme'] = preference;
  }
  const color = THEME_COLORS[resolveTheme(preference, readSystemTheme())];
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.content = color;
  }
}

const listeners = new Set<() => void>();
let watchedQuery: MediaQueryList | null = null;

/**
 * Apaga las transiciones durante el cambio de tema: si no, lo que tiene
 * transicion (botones, filas) se queda un instante con los colores del tema
 * anterior mientras el resto ya cambio. Se reactivan dos cuadros despues.
 */
function suppressTransitions(): void {
  if (typeof window.requestAnimationFrame !== 'function') {
    return;
  }
  const root = document.documentElement;
  root.classList.add('theme-switching');
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => root.classList.remove('theme-switching'));
  });
}

function emitChange(): void {
  suppressTransitions();
  applyToDocument();
  for (const listener of listeners) {
    listener();
  }
}

function onStorage(event: StorageEvent): void {
  // `key === null` es un `localStorage.clear()` hecho en otra pestana.
  if (event.key !== null && event.key !== THEME_STORAGE_KEY) {
    return;
  }
  // La otra pestana si pudo guardar: desde ahora manda lo guardado.
  unsavedPreference = null;
  emitChange();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    watchedQuery = systemQuery();
    watchedQuery?.addEventListener('change', emitChange);
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      watchedQuery?.removeEventListener('change', emitChange);
      watchedQuery = null;
      window.removeEventListener('storage', onStorage);
    }
  };
}

/** Cambia el tema en toda la app. Se aplica de inmediato aunque no se pueda guardar. */
export function setThemePreference(preference: ThemePreference): void {
  unsavedPreference = writeLocalPreference(THEME_STORAGE_KEY, preference) ? null : preference;
  emitChange();
}

export function useTheme(): ThemeState {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  return useMemo(
    () => ({
      preference: snapshot.preference,
      resolvedTheme: resolveTheme(snapshot.preference, snapshot.systemTheme),
      setPreference: setThemePreference,
    }),
    [snapshot],
  );
}

export interface ThemeProviderProps {
  readonly children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): ReactElement {
  // La suscripcion mantiene vivos los listeners del sistema y de otras pestanas
  // durante toda la vida de la app, aunque la pantalla abierta no lea el tema.
  useSyncExternalStore(subscribe, getSnapshot);
  // Por si el script de index.html no corrio (o el `<meta>` llego despues).
  useLayoutEffect(() => {
    applyToDocument();
  }, []);
  return <>{children}</>;
}
